import { ChevronLeftIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-left'
import { Button } from '@repo/studio-design-system-ui-button'
import * as React from 'react'
import { ComponentBoundary } from '@repo/studio-components-boundary'
import type { DefaultPlan } from 'payments/payments-dialog/lib/plans'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion'
import * as Dialog from 'primitives/dialog'
import { lazyComponent } from 'utils/lazy-component'
import { tw } from 'utils/classnames'
import { CoinTopUpDialogContent } from './components/content'

const PaymentsDialog = lazyComponent(() =>
  import('payments/payments-dialog').then(m => m.PaymentsDialog),
)

type Screen = { view: 'selection' } | { view: 'checkout'; plan: DefaultPlan }

interface CoinTopUpDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAutoStartSuccess?: () => void
  portalContainer?: HTMLElement | null
  zIndex?: number
}

export function CoinTopUpDialog({
  open,
  onOpenChange,
  onAutoStartSuccess,
  portalContainer,
  zIndex,
}: CoinTopUpDialogProps) {
  const [screen, setScreen] = React.useState<Screen>({ view: 'selection' })

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setScreen({ view: 'selection' })
    }
    onOpenChange(nextOpen)
  }

  function handleFallbackToCheckout(plan: DefaultPlan) {
    savePaymentModalEntryPoint(PAYMENT_ENTRY_POINTS.STUDIO)
    setScreen({ view: 'checkout', plan })
  }

  function handleClose() {
    onOpenChange(false)
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <ComponentBoundary fallbackLoading={null} fallbackError={null}>
        <Dialog.Portal forceMount container={portalContainer}>
          <Dialog.Body
            dismissable={false}
            onOpenAutoFocus={e => e.preventDefault()}
            zIndex={zIndex}
            overlayZIndex={zIndex}
          >
            <div
              className={tw(
                'rounded-4 relative flex flex-col w-full max-h-[90vh] max-w-[1111px] overflow-clip min-h-[570px] transition-colors duration-300 md:mx-4',
                screen.view === 'checkout' ? 'bg-service-white-white' : 'bg-gray-1000',
              )}
            >
              <LazyMotion features={domAnimation}>
                <AnimatePresence mode="wait" initial={false}>
                  {screen.view === 'selection' ? (
                    <m.div
                      key="selection"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ type: 'tween', duration: 0.2, ease: 'easeInOut' }}
                      className="flex-1 min-h-0 flex flex-col"
                    >
                      <CoinTopUpDialogContent
                        onFallbackToCheckout={handleFallbackToCheckout}
                        onClose={handleClose}
                        onAutoStartSuccess={onAutoStartSuccess}
                      />
                    </m.div>
                  ) : (
                    <m.div
                      key="checkout"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ type: 'tween', duration: 0.2, ease: 'easeInOut' }}
                      className="relative flex-1 min-h-0 flex flex-col"
                    >
                      <div
                        data-studio-theme="light"
                        className="absolute z-10 w-full top-0 bg-service-white-white pb-3 border-b border-b-gray-700-opacity"
                      >
                        <Button
                          className="mt-3 ml-3"
                          variant="tertiary"
                          size="sm"
                          onClick={() => setScreen({ view: 'selection' })}
                        >
                          <ChevronLeftIcon className="size-4" />
                          Back
                        </Button>
                      </div>
                      <div className="flex-1 min-h-0 w-full pt-12 grid">
                        <React.Suspense fallback={null}>
                          {/* TODO: Class override here is a bit nasty but otherwise we need to break apart the Payments Dialog into a proper compound component */}
                          <PaymentsDialog.component
                            className="[&_section]:bg-transparent min-h-0 px-0"
                            defaultPlan={screen.plan}
                          />
                        </React.Suspense>
                      </div>
                    </m.div>
                  )}
                </AnimatePresence>
              </LazyMotion>
            </div>
          </Dialog.Body>
        </Dialog.Portal>
      </ComponentBoundary>
    </Dialog.Root>
  )
}
